import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { buildApiUrl } from '../utils/api/config';
import './ManageDistricts.css';

function ManageDistricts() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [editingDistrictId, setEditingDistrictId] = useState('');
  const [editingBlockId, setEditingBlockId] = useState('');
  
  const [tab, setTab] = useState('district'); // 'district' or 'block'

  const [districtForm, setDistrictForm] = useState({
    name: '',
    state: 'उत्तर प्रदेश',
    description: ''
  });

  const [blockForm, setBlockForm] = useState({
    name: '',
    districtId: '',
    districtName: '',
    description: ''
  });

  const fetchDistricts = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl('/locations/districts'));
      const data = await response.json();
      if (data.success) {
        setDistricts(data.data);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      showToast('❌ Failed to load districts', 'error');
    }
  }, []);

  const fetchBlocksByDistrict = useCallback(async (districtId) => {
    try {
      const response = await fetch(buildApiUrl(`/locations/blocks/district/${districtId}`));
      const data = await response.json();
      if (data.success) {
        setBlocks(data.data);
      }
    } catch (error) {
      console.error('Error fetching blocks:', error);
      showToast('❌ Failed to load blocks', 'error');
    }
  }, []);

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  useEffect(() => {
    if (selectedDistrict) {
      fetchBlocksByDistrict(selectedDistrict);
    } else {
      setBlocks([]);
    }
  }, [fetchBlocksByDistrict, selectedDistrict]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDistrictInputChange = (e) => {
    const { name, value } = e.target;
    setDistrictForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlockInputChange = (e) => {
    const { name, value } = e.target;
    setBlockForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDistrictSelect = (e) => {
    const districtId = e.target.value;
    setSelectedDistrict(districtId);
    const selectedDist = districts.find(d => d._id === districtId);
    if (selectedDist) {
      setBlockForm(prev => ({
        ...prev,
        districtId,
        districtName: selectedDist.name
      }));
    }
  };

  const handleAddDistrict = async (e) => {
    e.preventDefault();

    if (!districtForm.name || !districtForm.state) {
      showToast('❌ District name and state are required', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(buildApiUrl(editingDistrictId ? `/locations/districts/${editingDistrictId}` : '/locations/districts/add'), {
        method: editingDistrictId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(districtForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add district');
      }

      showToast(editingDistrictId ? '✅ District updated successfully!' : '✅ District added successfully!');
      setEditingDistrictId('');
      setDistrictForm({
        name: '',
        state: 'उत्तर प्रदेश',
        description: ''
      });
      fetchDistricts();
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();

    if (!blockForm.name || !blockForm.districtId) {
      showToast('❌ Block name and district are required', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(buildApiUrl(editingBlockId ? `/locations/blocks/${editingBlockId}` : '/locations/blocks/add'), {
        method: editingBlockId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: blockForm.name,
          district: blockForm.districtId,
          districtName: blockForm.districtName,
          description: blockForm.description
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add block');
      }

      showToast(editingBlockId ? '✅ Block updated successfully!' : '✅ Block added successfully!');
      setEditingBlockId('');
      setBlockForm(prev => ({
        ...prev,
        name: '',
        description: ''
      }));
      fetchBlocksByDistrict(blockForm.districtId);
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  const handleEditDistrict = (district) => {
    setTab('district');
    setEditingDistrictId(district._id);
    setDistrictForm({
      name: district.name || '',
      state: district.state || 'उत्तर प्रदेश',
      description: district.description || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteDistrict = async (id) => {
    if (!window.confirm('क्या आप यह जिला हटाना चाहते हैं?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`/locations/districts/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete district');
      }
      showToast('✅ District deleted successfully!');
      if (selectedDistrict === id) {
        setSelectedDistrict('');
      }
      fetchDistricts();
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    }
  };

  const handleEditBlock = (block) => {
    setTab('block');
    setEditingBlockId(block._id);
    setSelectedDistrict(block.district);
    setBlockForm({
      name: block.name || '',
      districtId: block.district,
      districtName: block.districtName || '',
      description: block.description || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteBlock = async (id) => {
    if (!window.confirm('क्या आप यह ब्लॉक हटाना चाहते हैं?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`/locations/blocks/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete block');
      }
      showToast('✅ Block deleted successfully!');
      if (selectedDistrict) {
        fetchBlocksByDistrict(selectedDistrict);
      }
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    }
  };

  return (
    <>
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <AdminShell
        eyebrow="Coverage Mapping"
        title="Manage districts and blocks"
        description="Structure your location hierarchy with a cleaner workflow for district creation, block assignment, and regional browsing."
        primaryAction={{
          label: tab === 'district' ? 'Switch to blocks' : 'Switch to districts',
          onClick: () => setTab((current) => (current === 'district' ? 'block' : 'district'))
        }}
        secondaryAction={{ label: 'Dashboard', onClick: handleCancel }}
        meta={[
          { label: 'Districts', value: String(districts.length) },
          { label: 'Visible blocks', value: String(blocks.length) }
        ]}
      >
        <div className="manage-districts-container">
          <div className="districts-wrapper">

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-btn ${tab === 'district' ? 'active' : ''}`}
            onClick={() => setTab('district')}
          >
            📍 जिला जोड़ें
          </button>
          <button
            className={`tab-btn ${tab === 'block' ? 'active' : ''}`}
            onClick={() => setTab('block')}
          >
            📌 ब्लॉक जोड़ें
          </button>
        </div>

        {/* District Tab */}
        {tab === 'district' && (
          <div className="form-container">
            <h2>{editingDistrictId ? 'जिला संपादित करें' : 'नया जिला जोड़ें'}</h2>
            <form onSubmit={handleAddDistrict} className="form">
              <div className="form-group">
                <label>जिले का नाम *</label>
                <input
                  type="text"
                  name="name"
                  value={districtForm.name}
                  onChange={handleDistrictInputChange}
                  placeholder="जिले का नाम दर्ज करें..."
                  required
                />
              </div>

              <div className="form-group">
  <label>राज्य *</label>
  <select
    name="state"
    value={districtForm.state}
    onChange={handleDistrictInputChange}
    required
  >
    <option value="">राज्य चुनें</option>

    <option value="आंध्र प्रदेश">आंध्र प्रदेश</option>
    <option value="अरुणाचल प्रदेश">अरुणाचल प्रदेश</option>
    <option value="असम">असम</option>
    <option value="बिहार">बिहार</option>
    <option value="छत्तीसगढ़">छत्तीसगढ़</option>
    <option value="गोवा">गोवा</option>
    <option value="गुजरात">गुजरात</option>
    <option value="हरियाणा">हरियाणा</option>
    <option value="हिमाचल प्रदेश">हिमाचल प्रदेश</option>
    <option value="झारखंड">झारखंड</option>
    <option value="कर्नाटक">कर्नाटक</option>
    <option value="केरल">केरल</option>
    <option value="मध्य प्रदेश">मध्य प्रदेश</option>
    <option value="महाराष्ट्र">महाराष्ट्र</option>
    <option value="मणिपुर">मणिपुर</option>
    <option value="मेघालय">मेघालय</option>
    <option value="मिज़ोरम">मिज़ोरम</option>
    <option value="नागालैंड">नागालैंड</option>
    <option value="ओडिशा">ओडिशा</option>
    <option value="पंजाब">पंजाब</option>
    <option value="राजस्थान">राजस्थान</option>
    <option value="सिक्किम">सिक्किम</option>
    <option value="तमिलनाडु">तमिलनाडु</option>
    <option value="तेलंगाना">तेलंगाना</option>
    <option value="त्रिपुरा">त्रिपुरा</option>
    <option value="उत्तर प्रदेश">उत्तर प्रदेश</option>
    <option value="उत्तराखंड">उत्तराखंड</option>
    <option value="पश्चिम बंगाल">पश्चिम बंगाल</option>

  </select>
</div>

              <div className="form-group">
                <label>विवरण</label>
                <textarea
                  name="description"
                  value={districtForm.description}
                  onChange={handleDistrictInputChange}
                  placeholder="जिले का विवरण..."
                  rows={4}
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={loading} className="btn-submit">
                  {loading ? 'सेव हो रहा है...' : editingDistrictId ? '✓ जिला अपडेट करें' : '✓ जिला जोड़ें'}
                </button>
                <button type="button" onClick={editingDistrictId ? () => {
                  setEditingDistrictId('');
                  setDistrictForm({ name: '', state: 'उत्तर प्रदेश', description: '' });
                } : handleCancel} className="btn-cancel">
                  {editingDistrictId ? 'Reset' : '✕ रद्द करें'}
                </button>
              </div>
            </form>

            {/* Districts List */}
            <div className="list-container">
              <h3>मौजूदा जिले</h3>
              <div className="list">
                {districts.length > 0 ? (
                  districts.map(district => (
                    <div key={district._id} className="list-item">
                      <div className="item-info">
                        <h4>{district.name}</h4>
                        <p>{district.state}</p>
                      </div>
                      <div className="list-item-actions">
                        <button type="button" className="btn-cancel action-btn" onClick={() => handleEditDistrict(district)}>
                          Edit
                        </button>
                        <button type="button" className="btn-submit danger action-btn" onClick={() => handleDeleteDistrict(district._id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">कोई जिला नहीं है</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Block Tab */}
        {tab === 'block' && (
          <div className="form-container">
            <h2>{editingBlockId ? 'ब्लॉक संपादित करें' : 'नया ब्लॉक जोड़ें'}</h2>
            <form onSubmit={handleAddBlock} className="form">
              <div className="form-group">
                <label>जिला चुनें *</label>
                <select
                  value={selectedDistrict}
                  onChange={handleDistrictSelect}
                  required
                  className="form-select"
                >
                  <option value="">-- जिला चुनें --</option>
                  {districts.map(district => (
                    <option key={district._id} value={district._id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>ब्लॉक का नाम *</label>
                <input
                  type="text"
                  name="name"
                  value={blockForm.name}
                  onChange={handleBlockInputChange}
                  placeholder="ब्लॉक का नाम दर्ज करें..."
                  required
                />
              </div>

              <div className="form-group">
                <label>विवरण</label>
                <textarea
                  name="description"
                  value={blockForm.description}
                  onChange={handleBlockInputChange}
                  placeholder="ब्लॉक का विवरण..."
                  rows={4}
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={loading || !selectedDistrict} className="btn-submit">
                  {loading ? 'सेव हो रहा है...' : editingBlockId ? '✓ ब्लॉक अपडेट करें' : '✓ ब्लॉक जोड़ें'}
                </button>
                <button type="button" onClick={editingBlockId ? () => {
                  setEditingBlockId('');
                  setBlockForm(prev => ({ ...prev, name: '', description: '' }));
                } : handleCancel} className="btn-cancel">
                  {editingBlockId ? 'Reset' : '✕ रद्द करें'}
                </button>
              </div>
            </form>

            {/* Blocks List */}
            {selectedDistrict && (
              <div className="list-container">
                <h3>
                  {districts.find(d => d._id === selectedDistrict)?.name} जिले के ब्लॉक्स
                </h3>
                <div className="list">
                  {blocks.length > 0 ? (
                    blocks.map(block => (
                      <div key={block._id} className="list-item">
                        <div className="item-info">
                          <h4>{block.name}</h4>
                          <p>{block.districtName}</p>
                        </div>
                        <div className="list-item-actions">
                          <button type="button" className="btn-cancel action-btn" onClick={() => handleEditBlock(block)}>
                            Edit
                          </button>
                          <button type="button" className="btn-submit danger action-btn" onClick={() => handleDeleteBlock(block._id)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">कोई ब्लॉक नहीं है</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </AdminShell>
    </>
  );
}

export default ManageDistricts;
